<template>
  <section class="ft-section">
    <h2>1. D'où vient la structure ?</h2>
    <p class="ft-section__intro">
      Vous pourrez toujours modifier les champs ensuite
    </p>

    <div
      class="ft-sources"
      role="radiogroup"
      aria-label="Provenance de la structure"
    >
      <button
        type="button"
        class="ft-sources__card"
        role="radio"
        :aria-checked="source === 'manual' ? 'true' : 'false'"
        :class="{ 'is-active': source === 'manual' }"
        data-test="feature-type-source-manual"
        @click="$emit('manual')"
      >
        <strong>Créer manuellement</strong>
        <span>Je définis les champs moi-même</span>
      </button>

      <button
        type="button"
        class="ft-sources__card"
        role="radio"
        :aria-checked="source === 'file' ? 'true' : 'false'"
        :class="{ 'is-active': source === 'file' }"
        data-test="feature-type-source-file"
        @click="openFilePicker"
      >
        <strong>Importer un fichier</strong>
        <span>GeoJSON, JSON, CSV</span>
      </button>

      <button
        v-if="showCatalog"
        type="button"
        class="ft-sources__card"
        role="radio"
        :aria-checked="source === 'catalog' ? 'true' : 'false'"
        :class="{ 'is-active': source === 'catalog' }"
        data-test="feature-type-source-catalog"
        @click="$emit('catalog')"
      >
        <strong>Depuis le catalogue {{ catalogName || 'IDGO' }}</strong>
        <span>Jeux de données du catalogue connecté</span>
      </button>
    </div>

    <div
      v-if="source === 'file' && (fileSummary || importing || fileError)"
      class="ft-file"
      data-test="feature-type-file-summary"
    >
      <template v-if="fileSummary">
        <div class="ft-file__main">
          <p class="ft-file__name">
            {{ fileName }}
          </p>
          <p class="ft-file__meta">
            {{ featureLabel }} · géométrie : {{ fileSummary.geometryLabel }} · {{ fieldLabel }}
          </p>
          <label
            v-if="fileSummary.featureCount > 0"
            class="ft-file__check"
          >
            <input
              type="checkbox"
              :checked="importFeatures"
              data-test="feature-type-import-features"
              @change="$emit('update:importFeatures', $event.target.checked)"
            >
            <span>
              <strong>Importer aussi {{ importLabel }} du fichier</strong>
              <small>Décochée, seule la structure est créée, sans aucun signalement.</small>
            </span>
          </label>
        </div>
        <button
          type="button"
          class="ft-file__replace"
          data-test="feature-type-replace-file"
          @click="openFilePicker"
        >
          <i
            class="sync icon"
            aria-hidden="true"
          />
          Remplacer le fichier
        </button>
      </template>
      <p
        v-else-if="importing"
        class="ft-file__status"
      >
        Lecture du fichier…
      </p>
      <p
        v-if="fileError"
        class="ft-file__error"
      >
        {{ fileError }}
      </p>
    </div>

    <div
      v-if="source === 'catalog'"
      class="ft-catalog"
    >
      <p
        v-if="catalogLoading"
        class="ft-catalog__status"
      >
        Interrogation du catalogue…
      </p>
      <table v-else>
        <thead>
          <tr>
            <th>Organisation</th>
            <th>Dataset</th>
            <th>Ressource</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(resource, index) in paginatedResources"
            :key="`${resource.layer}-${index}`"
            :class="{ 'is-selected': isSelected(resource) }"
            @click="selectedResource = resource"
          >
            <td>{{ resource.organization_name }}</td>
            <td>{{ resource.dataset_name }}</td>
            <td>{{ resource.resource_name }}</td>
          </tr>
          <tr
            v-if="resources.length === 0"
            class="is-empty"
          >
            <td
              colspan="3"
              class="ft-catalog__empty"
            >
              Pas de données trouvées pour l'utilisateur {{ username || 'admin' }}
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="nbPages > 1"
        class="ft-catalog__pager"
      >
        <button
          type="button"
          :disabled="page === 1"
          @click="page -= 1"
        >
          Précédent
        </button>
        <span>{{ page }} / {{ nbPages }}</span>
        <button
          type="button"
          :disabled="page === nbPages"
          @click="page += 1"
        >
          Suivant
        </button>
      </div>

      <div class="ft-catalog__actions">
        <button
          type="button"
          class="ft-catalog__structure"
          :disabled="!selectedResource || importing"
          data-test="catalog-load-structure"
          @click="loadCatalog(false)"
        >
          Charger seulement la structure
        </button>
        <button
          type="button"
          class="ft-catalog__data"
          :disabled="!selectedResource || importing"
          data-test="catalog-load-data"
          @click="loadCatalog(true)"
        >
          Charger la structure avec les données
        </button>
      </div>
    </div>

    <input
      ref="file"
      type="file"
      class="ft-sources__input"
      accept=".geojson,.json,.csv,application/geo+json,application/json,text/csv"
      data-test="feature-type-structure-file"
      @change="$emit('file', $event)"
    >
  </section>
</template>

<script>
import { mapState } from 'vuex';
import miscAPI from '@/services/misc-api';

export default {
  name: 'FeatureTypeStructureSource',

  props: {
    source: {
      type: String,
      default: 'manual',
    },
    showCatalog: {
      type: Boolean,
      default: false,
    },
    catalogName: {
      type: String,
      default: '',
    },
    fileName: {
      type: String,
      default: '',
    },
    fileError: {
      type: String,
      default: '',
    },
    importing: {
      type: Boolean,
      default: false,
    },
    fileSummary: {
      type: Object,
      default: null,
    },
    importFeatures: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      resources: [],
      selectedResource: null,
      catalogLoading: false,
      catalogRequested: false,
      page: 1,
      pageSize: 8,
    };
  },

  computed: {
    ...mapState(['user']),

    username() {
      return this.user && this.user.username;
    },
    paginatedResources() {
      const start = (this.page - 1) * this.pageSize;
      return this.resources.slice(start, start + this.pageSize);
    },
    nbPages() {
      return Math.max(1, Math.ceil(this.resources.length / this.pageSize));
    },
    featureLabel() {
      const count = this.fileSummary ? this.fileSummary.featureCount : 0;
      return count > 1 ? `${count} entités` : `${count} entité`;
    },
    fieldLabel() {
      const count = this.fileSummary ? this.fileSummary.fieldCount : 0;
      return count > 1 ? `${count} champs` : `${count} champ`;
    },
    importLabel() {
      const count = this.fileSummary ? this.fileSummary.featureCount : 0;
      return count > 1 ? `les ${count} entités` : "l'entité";
    },
  },

  watch: {
    source: {
      immediate: true,
      handler(value) {
        if (value === 'catalog') {
          this.fetchCatalog();
        }
      },
    },
  },

  methods: {
    openFilePicker() {
      const previous = this.source;
      this.$emit('file-intent');
      // The file dialog does not emit change when it is cancelled.
      const onFocus = () => {
        window.setTimeout(() => {
          if (
            previous !== 'file' &&
            !this.importing &&
            !this.fileSummary &&
            !this.fileError
          ) {
            this.$emit(previous === 'catalog' ? 'catalog' : 'manual');
          }
        }, 300);
      };
      window.addEventListener('focus', onFocus, { once: true });
      if (this.$refs.file) {
        this.$refs.file.click();
      }
    },
    isSelected(resource) {
      return this.selectedResource && resource.layer === this.selectedResource.layer;
    },
    fetchCatalog() {
      if (this.catalogRequested || !this.showCatalog) {
        return;
      }
      this.catalogRequested = true;
      this.catalogLoading = true;
      miscAPI.getIdgoCatalog(this.username).then((data) => {
        this.resources = data && data.layers ? data.layers : [];
        this.catalogLoading = false;
      });
    },
    loadCatalog(withFeatures) {
      if (!this.selectedResource) {
        return;
      }
      this.$emit('load-catalog', {
        resource: this.selectedResource,
        withFeatures,
      });
    },
  },
};
</script>

<style lang="less" scoped>
.ft-section {
  background: #fff;
  border: 1px solid #e4e7ee;
  border-radius: 8px;
  padding: 1.25rem 1.4rem 1.35rem;
  box-shadow: 0 1px 2px rgba(20, 30, 50, 0.04);

  h2 {
    margin: 0 0 0.3rem;
    font-size: 18px;
    font-weight: 600;
    line-height: 1;
    letter-spacing: 0;
    color: #1c2430;
  }
}

.ft-section__intro {
  margin: 0 0 1rem;
  color: #6b7280;
}

.ft-sources {
  display: flex;
  gap: 0.75rem;
}

.ft-sources__card {
  flex: 1 1 0;
  text-align: left;
  cursor: pointer;
  background: #fff;
  border: 1px solid #d5d8e0;
  border-radius: 6px;
  padding: 0.85rem 1rem;
  color: #1c2430;
  font: inherit;

  strong {
    display: block;
    font-weight: 600;
    margin-bottom: 0.2rem;
  }

  span {
    display: block;
    color: #6b7280;
  }

  &.is-active {
    background: #3A468E;
    border-color: #3A468E;
    color: #fff;
    box-shadow: none;

    span {
      color: rgba(255, 255, 255, 0.85);
    }
  }
}

.ft-sources__input {
  display: none;
}

.ft-file {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 0.9rem;
  padding: 0.9rem 1rem;
  background: #f6f7f9;
  border: 1px solid #e4e7ee;
  border-radius: 6px;
}

.ft-file__name {
  margin: 0;
  font-weight: 700;
  color: #1c2430;
}

.ft-file__meta {
  margin: 0.25rem 0 0;
  color: #5c6b82;
}

.ft-file__check {
  display: flex;
  gap: 0.6rem;
  align-items: flex-start;
  margin-top: 0.75rem;
  cursor: pointer;

  input {
    -webkit-appearance: none;
    appearance: none;
    position: relative;
    flex: none;
    width: 1.05rem;
    height: 1.05rem;
    margin: 0.2rem 0 0;
    border: 1.5px solid #c5cad3;
    border-radius: 2px;
    background: #fff;
    cursor: pointer;

    &:checked {
      border-color: #B52222;
    }

    &:checked::after {
      content: '';
      position: absolute;
      left: 0.3rem;
      top: 0.08rem;
      width: 0.3rem;
      height: 0.58rem;
      border: solid #B52222;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
  }

  strong {
    display: block;
    font-weight: 600;
    color: #1c2430;
  }

  small {
    display: block;
    margin-top: 0.12rem;
    color: #8b919c;
  }
}

.ft-file__replace {
  flex: 0 0 auto;
  border: 1px solid #c5cad3;
  background: #fff;
  border-radius: 4px;
  padding: 0.45rem 0.75rem;
  font: inherit;
  color: #3A468E;
  cursor: pointer;
  white-space: nowrap;

  i {
    margin-right: 0.25rem;
  }
}

.ft-file__status,
.ft-file__error,
.ft-catalog__status {
  margin: 0;
}

.ft-file__error {
  flex: 1 0 100%;
  color: #c0392b;
  margin-top: 0.45rem;
}

.ft-catalog {
  margin-top: 0.9rem;

  table {
    width: 100%;
    border-collapse: collapse;
    border: 1px solid #e1e4ea;
  }

  th {
    text-align: left;
    background: #f7f8fa;
    font-weight: 600;
    color: #3c4656;
    padding: 0.55rem 0.7rem;
    border-bottom: 1px solid #e1e4ea;
  }

  td {
    padding: 0.55rem 0.7rem;
    border-top: 1px solid #eef0f3;
    color: #1c2430;
  }

  tbody tr:not(.is-empty) {
    cursor: pointer;
  }

  tr.is-empty {
    cursor: default;
  }

  tr.is-selected td {
    background: #eef2f8;
  }
}

.ft-catalog__empty {
  text-align: center;
  color: #6b7280;
  cursor: default;
}

.ft-catalog__pager {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.6rem;
  margin-top: 0.55rem;

  button {
    border: 1px solid #d5d8e0;
    background: #fff;
    border-radius: 4px;
    padding: 0.25rem 0.55rem;
    font: inherit;
    cursor: pointer;

    &:disabled {
      opacity: 0.45;
      cursor: default;
    }
  }
}

.ft-catalog__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  margin-top: 0.85rem;
}

.ft-catalog__structure,
.ft-catalog__data {
  border-radius: 4px;
  padding: 0.5rem 0.8rem;
  font: inherit;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
}

.ft-catalog__structure {
  background: #fff;
  color: #3A468E;
  border: 1px solid #3A468E;
}

.ft-catalog__data {
  background: #3A468E;
  color: #fff;
  border: 0;
}

@media (max-width: 800px) {
  .ft-sources,
  .ft-file,
  .ft-catalog__actions {
    flex-direction: column;
  }
}
</style>
